import { createPortal } from "react-dom";
import { motion as Motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import TicketsHeader from "./TicketsHeader";
import { useSelector } from "react-redux";

// Skeleton
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link } from "react-router-dom";

// Utils
import { scrollToTop } from "@/lib/utilsUI";
import { getColorByEstadoInvitacion } from "@/utils/invitacionUtils";

// Services
import api from "@/services/api";

// Assets
import bckWoop from "@/assets/Logo-AppWhoop.webp";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const TIME_FILTERS = [
  { label: "Recientes", value: "recent" },
  { label: "Ayer", value: "yesterday" },
  { label: "Esta semana", value: "thisweek" },
  { label: "Semana anterior", value: "lastweek" },
];

const MODAL_TABS = [
  { label: "Tickets", value: "tickets", icon: "fa-solid fa-ticket" },
  { label: "Invitaciones", value: "invitaciones", icon: "fa-solid fa-envelope-open-text" },
];

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────

function ModalTabs({ activeTab, setActiveTab }) {
  return (
    <div className="flex border-b border-aw-bluegray">
      {MODAL_TABS.map((tab) => (
        <button
          key={tab.value}
          onClick={() => setActiveTab(tab.value)}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-xs font-semibold transition-all border-b-2 ${
            activeTab === tab.value
              ? "border-aw-green text-aw-green bg-aw-green/10"
              : "border-transparent text-gray-400 hover:text-white"
          }`}
        >
          <FontAwesomeIcon icon={tab.icon} />
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function TicketItem({ ticket, onClose }) {
  const { uid } = useSelector((state) => state.auth.user) ?? {};
  return (
    <div className="flex items-center gap-3 py-1.5 border-b border-aw-bluedark-50 last:border-b-0">
      {ticket.type === "bought" ? (
        <img
          src={ticket.img}
          alt="Ticket"
          className="w-14 h-14 rounded-lg object-cover bg-aw-bluegray/60 shrink-0"
        />
      ) : (
        <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-aw-bluedark-50 shrink-0">
          <FontAwesomeIcon icon={faBell} className="text-2xl text-aw-green" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <span className="font-bold text-white text-sm block truncate">{ticket.title}</span>
        <div className="text-xs text-gray-400 mt-0.5">{ticket.subtitle}</div>
        {ticket.type === "bought" && (
          <div className="flex items-center justify-between gap-2 mt-1 text-xs text-gray-200">
            <span>
              <FontAwesomeIcon icon={faBell} className="mr-1" />
              {ticket.time}
            </span>
            <Link
              onClick={onClose}
              to={`/perfil/${uid}/tickets/${ticket.uid}`}
              className="bg-aw-green text-aw-bluedark px-2 py-1 rounded-full text-[0.7rem] font-semibold ml-auto shrink-0"
            >
              Ver Ticket
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function TicketsList({ tickets, onClose }) {
  return (
    <div className="mt-2">
      {tickets.map((ticket) => (
        <TicketItem key={ticket.id} ticket={ticket} onClose={onClose} />
      ))}
    </div>
  );
}

function InvitacionItem({ inv, onClose, userUid }) {
  const colores = getColorByEstadoInvitacion(inv.estado);
  return (
    <div className="flex items-center gap-3 py-1.5 border-b border-aw-bluedark-50 last:border-b-0">
      <img
        src={inv.imgEvento || bckWoop}
        alt={inv.eventoNombre || "Evento"}
        className="w-14 h-14 rounded-lg object-cover bg-aw-bluegray/60 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <span className="font-bold text-white text-sm block truncate">{inv.eventoNombre}</span>
        <div className="text-xs text-gray-400 mt-0.5">{inv.tipoTicketNombre}</div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <span className={`text-xs font-semibold ${colores.text}`}>{colores.label}</span>
          <Link
            onClick={onClose}
            to={`/perfil/${userUid}/invitaciones/${inv.uid}`}
            className="bg-aw-green text-aw-bluedark px-2 py-1 rounded-full text-[0.7rem] font-semibold ml-auto shrink-0"
          >
            Ver Invitacion
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────

export default function TicketsModal({
  isOpen,
  onClose,
  tickets = [],
  isLoading = false,
  hasMore = false,
  onLoadMore,
}) {
  const { uid: userUid } = useSelector((state) => state.auth.user) ?? {};

  const [activeTab, setActiveTab] = useState("tickets");
  const [activeFilter, setActiveFilter] = useState("recent");

  // ── Invitaciones local state ──
  const [invitaciones, setInvitaciones] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [hasMoreInv, setHasMoreInv] = useState(false);
  const [invPage, setInvPage] = useState(1);

  // Reset invitaciones cuando el modal se cierra
  useEffect(() => {
    if (!isOpen) {
      setInvitaciones([]);
      setInvPage(1);
      setHasMoreInv(false);
      setActiveTab("tickets");
    }
  }, [isOpen]);

  // Cargar invitaciones al cambiar a la tab (lazy)
  useEffect(() => {
    if (isOpen && activeTab === "invitaciones" && userUid && invitaciones.length === 0 && !invLoading) {
      loadInvitaciones(1, false);
    }
  }, [isOpen, activeTab, userUid]);

  const loadInvitaciones = async (page = 1, append = false) => {
    if (!userUid || invLoading) return;
    setInvLoading(true);
    try {
      const { data } = await api.get(`/invitaciones/usuario/${userUid}`, {
        params: { seccion: "activas", page, limit: 10 },
      });
      // Solo estados aceptados (excluir 'pendiente')
      const items = (data.items || []).filter((item) => item.estado !== "pendiente");
      if (append) {
        setInvitaciones((prev) => [...prev, ...items]);
      } else {
        setInvitaciones(items);
      }
      setHasMoreInv(data.page * data.limit < data.total);
      setInvPage(page);
    } catch (err) {
      console.error("Error loading invitaciones:", err);
    } finally {
      setInvLoading(false);
    }
  };

  const loadMoreInvitaciones = () => {
    if (hasMoreInv && !invLoading) {
      loadInvitaciones(invPage + 1, true);
    }
  };

  const isLoadingContent = activeTab === "tickets" ? isLoading : invLoading;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <Motion.div
          key="tickets-modal-bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[85] bg-aw-bluedark sm:bg-aw-bluedark-50 flex items-start justify-end"
          onClick={onClose}
        >
          <Motion.div
            key="tickets-modal"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="w-full max-w-md h-[92.5dvh] sm:h-full bg-aw-bluedark rounded-l-2xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-4 border-b border-aw-bluedark-50">
              <TicketsHeader
                onClose={onClose}
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                TIME_FILTERS={TIME_FILTERS}
              />
            </div>

            {/* Tabs */}
            <ModalTabs activeTab={activeTab} setActiveTab={setActiveTab} />

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 pb-6 scrollbar-box">
              {isLoadingContent ? (
                <SkeletonTheme baseColor="#1a2332" highlightColor="#2a3544">
                  <div className="mt-2 space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 py-1.5 border-b border-aw-bluegray"
                      >
                        <Skeleton width={56} height={56} borderRadius={8} />
                        <div className="flex-1 space-y-2">
                          <Skeleton width="75%" height={16} />
                          <Skeleton width="50%" height={12} />
                        </div>
                      </div>
                    ))}
                  </div>
                </SkeletonTheme>
              ) : activeTab === "tickets" ? (
                tickets.length === 0 ? (
                  <div className="mt-8 text-center text-gray-400">
                    <FontAwesomeIcon icon={faBell} className="text-4xl mb-4" />
                    <h4 className="font-semibold text-white">No tienes tickets comprados</h4>
                    <p className="text-sm">Explora eventos disponibles y asegura tu lugar</p>
                    <Link
                      onClick={() => { onClose(); scrollToTop(); }}
                      to="/"
                      className="mt-4 inline-block px-4 py-2 bg-aw-green text-aw-bluedark rounded-full text-sm font-semibold hover:scale-95 transition-transform duration-300"
                    >
                      Explorar Eventos
                    </Link>
                  </div>
                ) : (
                  <>
                    <TicketsList tickets={tickets} onClose={onClose} />
                    {hasMore && (
                      <div className="mt-4 flex justify-center">
                        <button
                          onClick={onLoadMore}
                          disabled={isLoading}
                          className="px-4 py-2 bg-aw-green text-aw-bluedark rounded-full text-sm font-semibold hover:bg-aw-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                          {isLoading ? (
                            <span className="flex items-center gap-2">
                              <FontAwesomeIcon icon="fa-solid fa-spinner" className="animate-spin" />
                              Cargando...
                            </span>
                          ) : (
                            "Cargar más"
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )
              ) : invitaciones.length === 0 ? (
                <div className="mt-8 text-center text-gray-400">
                  <FontAwesomeIcon
                    icon="fa-solid fa-envelope-open-text"
                    className="text-4xl mb-4 text-aw-green/50"
                  />
                  <h4 className="font-semibold text-white">No tienes invitaciones activas</h4>
                  <p className="text-sm">Aqui apareceran tus invitaciones aceptadas</p>
                </div>
              ) : (
                <>
                  <div className="mt-2">
                    {invitaciones.map((inv) => (
                      <InvitacionItem
                        key={inv.uid}
                        inv={inv}
                        onClose={onClose}
                        userUid={userUid}
                      />
                    ))}
                  </div>
                  {hasMoreInv && (
                    <div className="mt-4 flex justify-center">
                      <button
                        onClick={loadMoreInvitaciones}
                        disabled={invLoading}
                        className="px-4 py-2 bg-aw-green text-aw-bluedark rounded-full text-sm font-semibold hover:bg-aw-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {invLoading ? (
                          <span className="flex items-center gap-2">
                            <FontAwesomeIcon icon="fa-solid fa-spinner" className="animate-spin" />
                            Cargando...
                          </span>
                        ) : (
                          "Cargar más"
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Motion.div>
        </Motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
